import { ReloadOutlined } from '@ant-design/icons';
import { ApiError, type MaterialRow, type ShortageAuditStatus, type ShortageHandleResult, type ShortageRow } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import { hq } from '../session';
import { TipText, sumWidth } from '@hq/ui-kit';
import ShortageReplenish from './ShortageReplenish';

interface HandleFormValues {
  handleResult: ShortageHandleResult;
  handleMaterialId?: string;
  linkPurchaseOrderId?: string;
  remark?: string;
}

/**
 * 替代商品快搜：缺货处理时手填物资主键几乎不可能记住，且写错后不会报错，
 * 只会把替代结论挂到另一个物资上。改为按名称/编码搜索。后端无快搜接口时继续用 ID 录入。
 */
function SubstituteMaterialSelect({
  value,
  onChange
}: {
  value?: string;
  onChange?: (value?: string) => void;
}) {
  const [keyword, setKeyword] = useState('');
  const options = useQuery({
    queryKey: ['shortage', 'material', keyword],
    queryFn: () => hq.material.list({ keyword: keyword || undefined, size: 20 })
  });
  const items = (options.data?.items ?? []).map((row: MaterialRow) => ({
    value: row.id,
    label: `${row.materialName ?? ''}（${row.materialCode ?? ''}）`
  }));
  return (
    <Select
      showSearch
      value={value}
      placeholder="搜索物资名称 / 编码"
      filterOption={false}
      onSearch={setKeyword}
      onChange={(next) => onChange?.(next)}
      loading={options.isFetching}
      options={value && !items.some((item) => item.value === value) ? [{ value, label: value }, ...items] : items}
    />
  );
}

/**
 * 关联采购订单快搜：此前只能在提示里让用户"从采购订单列表复制主键"，
 * 19 位雪花 ID 手抄极易输错且后端不会报错（只把缺货单挂到另一张订单上）。
 * 后端 `GET /wl/purchase?keyword=` 支持按单号/供应商名模糊查。
 */
function PurchaseOrderSelect({
  value,
  onChange
}: {
  value?: string;
  onChange?: (value?: string) => void;
}) {
  const [keyword, setKeyword] = useState('');
  const options = useQuery({
    queryKey: ['shortage', 'purchase-order', keyword],
    queryFn: () => hq.procurement.purchases.list({ keyword: keyword || undefined, size: 20 })
  });
  const items = (options.data?.items ?? []).map((row) => ({
    value: row.id,
    label: `${row.bizNo}（${row.supplierName ?? '未知供应商'}）`
  }));
  return (
    <Select
      showSearch
      allowClear
      value={value || undefined}
      placeholder="搜索采购单号 / 供应商"
      filterOption={false}
      onSearch={setKeyword}
      onChange={(next) => onChange?.(next)}
      loading={options.isFetching}
      // 编辑态回显：已选订单不在当前搜索结果里时保留原始 ID，避免变成空白
      options={value && !items.some((item) => item.value === value) ? [{ value, label: value }, ...items] : items}
    />
  );
}

const URGENCY_META: Record<string, { color: string; text: string }> = {
  NORMAL: { color: 'default', text: '普通' },
  URGENT: { color: 'orange', text: '加急' },
  EMERGENCY: { color: 'red', text: '应急' }
};

const STATUS_META: Record<string, { color: string; text: string }> = {
  SUBMITTED: { color: 'blue', text: '待处理' },
  AUDITING: { color: 'gold', text: '处理中' },
  HANDLED: { color: 'green', text: '已处理' },
  REJECTED: { color: 'default', text: '已驳回' },
  CLOSED: { color: 'default', text: '已关闭' }
};

/**
 * 缺货申请处理（运营端）：按响应时限优先展示，处理方式为
 * 加目录 / 替代商品 / 转采购 / 驳回，处理结果可回填采购订单号。
 */
export default function ShortageOps() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>('SUBMITTED');
  const [current, setCurrent] = useState<ShortageRow | null>(null);
  const [form] = Form.useForm<HandleFormValues>();

  const list = useQuery({
    queryKey: ['ops', 'shortage', status],
    placeholderData: keepPreviousData,
    queryFn: () => hq.shortage.list({ size: 50, auditStatus: status as ShortageAuditStatus | '' })
  });

  const handle = useMutation({
    mutationFn: (values: HandleFormValues) => {
      if (!current) throw new Error('未选择单据');
      return hq.shortage.handle(current.id, {
        handleResult: values.handleResult,
        // 雪花 id 一律按字符串传：走 Number 会在 19 位上丢精度，静默关联错物资/订单
        handleMaterialId: values.handleMaterialId || '0',
        linkPurchaseOrderId: values.linkPurchaseOrderId || '0',
        remark: values.remark
      });
    },
    onSuccess: () => {
      message.success('处理完成');
      setCurrent(null);
      form.resetFields();
      void queryClient.invalidateQueries({ queryKey: ['ops', 'shortage'] });
    }
  });

  const rows = list.data?.items ?? [];
  const error = list.error instanceof ApiError ? list.error.friendlyMessage : null;

  const columns: ColumnsType<ShortageRow> = [
    { title: '单号', dataIndex: 'bizNo', width: 190 },
    { title: '机构', dataIndex: 'orgId', width: 110 },
    {
      title: '商品',
      ellipsis: { showTitle: false },
      // 单行展示：规格收进悬浮提示，避免行高被拉高
      render: (_, row) => {
        const name = row.materialName ?? row.materialNameReq ?? '-';
        return <TipText text={name} tip={row.specReq ? `${name} · ${row.specReq}` : undefined} />;
      }
    },
    {
      title: '数量',
      dataIndex: 'reqQty',
      width: 100,
      align: 'right',
      render: (value: number, row) => `${value} ${row.unitName ?? ''}`
    },
    {
      title: '紧急度',
      dataIndex: 'urgency',
      width: 90,
      render: (value: string) => <Tag color={URGENCY_META[value]?.color}>{URGENCY_META[value]?.text ?? value}</Tag>
    },
    {
      title: '响应时限',
      dataIndex: 'respondDeadline',
      width: 170,
      render: (value: string | null, row) => {
        if (!value) return '-';
        const overdue = !row.respondAt && dayjs(value).isBefore(dayjs());
        return (
          <Typography.Text type={overdue ? 'danger' : undefined}>
            {formatDisplayTime(value)}
            {overdue ? '（超时）' : ''}
          </Typography.Text>
        );
      }
    },
    {
      title: '状态',
      dataIndex: 'auditStatus',
      width: 100,
      render: (value: string) => <Tag color={STATUS_META[value]?.color}>{STATUS_META[value]?.text ?? value}</Tag>
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, row) =>
        row.auditStatus === 'SUBMITTED' || row.auditStatus === 'AUDITING' ? (
          <Button
            type="link"
            onClick={() => {
              setCurrent(row);
              form.setFieldsValue({ handleResult: 'PURCHASE' });
            }}
          >
            处理
          </Button>
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        )
    }
  ];

  return (
    <Card
      className="page-card"
      title="缺货处理"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => void list.refetch()} loading={list.isFetching}>
          刷新
        </Button>
      }
    >
      <Tabs
        items={[
          {
            key: 'apply',
            label: '缺货申请',
            children: (
              <>
                <Space style={{ marginBottom: 12 }}>
                  <Typography.Text type="secondary">状态</Typography.Text>
                  <Select
                    value={status}
                    style={{ width: 140 }}
                    onChange={setStatus}
                    options={[
                      { value: 'SUBMITTED', label: '待处理' },
                      { value: 'HANDLED', label: '已处理' },
                      { value: 'REJECTED', label: '已驳回' },
                      { value: '', label: '全部' }
                    ]}
                  />
                </Space>

                {error ? (
                  <Alert type="error" showIcon style={{ marginBottom: 16 }} message="加载失败" description={error} />
                ) : null}

                <Table<ShortageRow>
                  rowKey="id"
                  size="middle"
                  columns={columns}
                  dataSource={rows}
                  loading={list.isPending}
                  pagination={false}
                  scroll={{ x: sumWidth(columns) }}
                  locale={{ emptyText: '暂无缺货申请' }}
                />
              </>
            )
          },
          {
            key: 'replenish',
            label: '补货建议',
            children: <ShortageReplenish />
          }
        ]}
      />

      <Modal
        title={`处理缺货申请 ${current?.bizNo ?? ''}`}
        open={current !== null}
        onCancel={() => setCurrent(null)}
        onOk={() => form.submit()}
        confirmLoading={handle.isPending}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${current?.materialName ?? current?.materialNameReq ?? ''} × ${current?.reqQty ?? 0} ${
            current?.unitName ?? ''
          }`}
        />
        <Form<HandleFormValues> form={form} layout="vertical" onFinish={(values) => handle.mutate(values)}>
          <Form.Item name="handleResult" label="处理方式" rules={[{ required: true, message: '请选择处理方式' }]}>
            <Select
              options={[
                { value: 'ADD_CATALOG', label: '加入商品目录' },
                { value: 'SUBSTITUTE', label: '替代商品' },
                { value: 'PURCHASE', label: '转采购' },
                { value: 'REJECT', label: '驳回' }
              ]}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.handleResult !== next.handleResult}>
            {({ getFieldValue }) =>
              getFieldValue('handleResult') === 'SUBSTITUTE' ? (
                <Form.Item
                  name="handleMaterialId"
                  label="替代商品"
                  rules={[{ required: true, message: '请选择替代商品' }]}
                >
                  <SubstituteMaterialSelect />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item name="linkPurchaseOrderId" label="关联采购订单（选填）" tooltip="按单号或供应商名搜索">
            <PurchaseOrderSelect />
          </Form.Item>
          <Form.Item name="remark" label="处理说明">
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
